import { useEffect, useState } from 'react';
import TileCard from '../TileCard';
import { getE2eResults, prefetch } from '../../api/client';
import type { Env, E2eResults } from '../../types';

const HELP =
  'Outcome of the most recent end-to-end test run against this environment ' +
  '(validation suite + contact/case lifecycle). Green = every check passed, ' +
  'red = at least one check failed. Open the detail page for the per-check table ' +
  'and an explanation of everything tested. Runs nightly on TEST and on demand ' +
  'for other estates.';

export default function E2eResultsSummaryTile({ env, detailsHref }: { env: Env; detailsHref?: string }) {
  const [data, setData] = useState<E2eResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getE2eResults(env)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Warm the same endpoint the detail page reads so it renders from cache.
    prefetch('e2e-results', { env });
    return () => {
      cancelled = true;
    };
  }, [env]);

  const generatedFooter = data ? (
    <span className="tile-footer-row">
      <span>
        {data.runId ? `Run ${data.runId}` : `As of ${new Date(data.generatedAt).toLocaleTimeString()}`}
        {data.startedAt ? ` · ${new Date(data.startedAt).toLocaleString()}` : ''}
      </span>
      {detailsHref && (
        <a href={`#${detailsHref}`} className="tile-details-link" target="_blank" rel="noopener noreferrer">
          View details →
        </a>
      )}
    </span>
  ) : undefined;

  const total = data?.total ?? 0;
  const passed = data?.passed ?? 0;
  const failed = data?.failed ?? 0;
  const passRate = total ? ((passed / total) * 100).toFixed(0) : '—';

  return (
    <TileCard
      title={`E2E tests — latest run (${env.toUpperCase()})`}
      help={HELP}
      hideHelp={!!detailsHref}
      loading={loading}
      error={error}
      footer={generatedFooter}
    >
      {data?.status === 'not-configured' && <p className="tile-empty">{data.message}</p>}
      {data?.status === 'error' && <div className="tile-error">Query failed: {data.message}</div>}
      {data?.status === 'ok' && (
        <dl className="stats-grid">
          <div>
            <dt>Checks</dt>
            <dd>{total}</dd>
          </div>
          <div className="ok">
            <dt>Passed</dt>
            <dd>{passed}</dd>
          </div>
          <div className={failed > 0 ? 'bad' : 'ok'}>
            <dt>Failed</dt>
            <dd>{failed}</dd>
          </div>
          <div className={failed > 0 ? 'bad' : 'ok'}>
            <dt>Pass rate</dt>
            <dd>{passRate}%</dd>
          </div>
        </dl>
      )}
    </TileCard>
  );
}
